import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

const formatNumber = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "Unavailable";
  return numeric.toLocaleString();
};

const hasCalculatedMetrics = (row) => Number(row?.files_with_loc_metrics || 0) > 0;

const getMetricValue = (row, key) => {
  if (!hasCalculatedMetrics(row)) return "Unavailable";
  return formatNumber(row?.[key]);
};

const buildIncompleteLabel = (row) => {
  const skipped = Number(row?.skipped_files || 0);
  const tracked = Number(row?.tracked_files || row?.files_changed || 0);
  if (!tracked) return "No tracked files";
  if (!skipped) return "Complete";
  return `${skipped.toLocaleString()} unavailable`;
};

const metricCategoryOrder = [
  ["app_code", "App code"],
  ["documentation", "Docs"],
  ["artifact", "Artifacts"],
  ["unavailable", "Unavailable"],
];

const buildReasonLabel = (counts = {}) => {
  const entries = Object.entries(counts || {}).filter(([, value]) => Number(value || 0) > 0);
  if (!entries.length) return "No unavailable files";
  return entries
    .map(([key, value]) => `${key.replaceAll("_", " ")}: ${formatNumber(value)}`)
    .join("; ");
};

const CategoryMetricChips = ({ categoryTotals = {} }) => (
  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
    {metricCategoryOrder.map(([key, label]) => {
      const item = categoryTotals?.[key] || {};
      const files = Number(item.files || 0);
      if (!files) return null;
      const unavailable = Number(item.unavailable_files || 0);
      const added = formatNumber(item.lines_added || 0);
      const removed = formatNumber(item.lines_removed || 0);
      const suffix = unavailable ? `, unavailable ${formatNumber(unavailable)}` : "";
      return (
        <Chip
          key={key}
          size="small"
          variant="outlined"
          color={key === "app_code" ? "success" : key === "unavailable" ? "warning" : "default"}
          label={`${label}: +${added} / -${removed} (${formatNumber(files)} files${suffix})`}
        />
      );
    })}
  </Stack>
);

const FullChangeLocMetricsPanel = ({ fullChangeId }) => {
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [metrics, setMetrics] = useState(null);
  const [statusText, setStatusText] = useState("");

  const loadMetrics = async ({ recalculate = false, source = "manual" } = {}) => {
    if (!fullChangeId) {
      setMetrics(null);
      setStatusText("");
      return;
    }

    if (recalculate) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError("");
    try {
      const url = recalculate
        ? "/api/dev-ide/full-change/loc-metrics/recalculate"
        : `/api/dev-ide/full-change/loc-metrics?full_change_id=${encodeURIComponent(fullChangeId)}`;
      const res = await fetch(url, recalculate
        ? {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ full_change_id: fullChangeId }),
        }
        : undefined);
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || (data.errors || []).join("; ") || "Unable to load LOC metrics.");
      }
      setMetrics(data);
      const generatedAt = data.generated_at ? new Date(data.generated_at).toLocaleString() : "";
      if (recalculate) {
        setStatusText(`${source === "review_workspace_refresh" ? "Refreshed after review data reload" : "Refreshed"}${generatedAt ? ` at ${generatedAt}` : ""}.`);
      } else {
        setStatusText(generatedAt ? `Current as of ${generatedAt}.` : "Current from latest tracked review data.");
      }
    } catch (err) {
      setError(err.message || "Unable to load LOC metrics.");
      setStatusText("Metric refresh failed; review and save flows are unaffected.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadMetrics();
  }, [fullChangeId]);

  useEffect(() => {
    const handleRefresh = (event) => {
      const eventFullChangeId = event?.detail?.fullChangeId;
      if (String(eventFullChangeId || "") !== String(fullChangeId || "")) return;
      loadMetrics({ recalculate: true, source: event?.detail?.source || "review_workspace_refresh" });
    };
    window.addEventListener("devide:full-change-loc-refresh", handleRefresh);
    return () => window.removeEventListener("devide:full-change-loc-refresh", handleRefresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullChangeId]);

  const summary = metrics?.summary || metrics?.totals || {};
  const linkedRows = useMemo(() => metrics?.linked_changes || metrics?.per_linked_change || [], [metrics]);
  const hasAnyMetrics = Number(summary.files_with_loc_metrics || 0) > 0;

  if (!fullChangeId) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.5} sx={{ mb: 1.5 }}>
        <Box>
          <Typography variant="h6">LOC metrics</Typography>
          <Typography variant="body2" color="text.secondary">
            Advisory line counts from saved DEV IDE diff/review data. New files count all after-content lines; deleted files count all before-content lines.
          </Typography>
          {statusText ? (
            <Typography variant="caption" color="text.secondary">
              {statusText}
            </Typography>
          ) : null}
        </Box>
        <Button
          variant="outlined"
          size="small"
          onClick={() => loadMetrics({ recalculate: true })}
          disabled={loading || refreshing}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          {refreshing ? "Refreshing..." : "Refresh metrics"}
        </Button>
      </Stack>

      {error ? <Alert severity="warning" sx={{ mb: 1.5 }}>{error}</Alert> : null}
      {loading ? (
        <Stack direction="row" alignItems="center" spacing={1}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">Loading LOC metrics...</Typography>
        </Stack>
      ) : (
        <>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
            <Chip label={`Added: ${hasAnyMetrics ? formatNumber(summary.lines_added) : "Unavailable"}`} color={hasAnyMetrics ? "success" : "default"} variant={hasAnyMetrics ? "filled" : "outlined"} />
            <Chip label={`Removed: ${hasAnyMetrics ? formatNumber(summary.lines_removed) : "Unavailable"}`} color={hasAnyMetrics ? "warning" : "default"} variant="outlined" />
            <Chip label={`Net: ${hasAnyMetrics ? formatNumber(summary.net_lines) : "Unavailable"}`} color="primary" variant="outlined" />
            <Chip label={`Files: ${formatNumber(summary.files_changed || 0)}`} variant="outlined" />
            <Chip label={`Metric files: ${formatNumber(summary.files_with_loc_metrics || 0)}`} color={hasAnyMetrics ? "success" : "default"} variant="outlined" />
            <Chip label={`New files: ${formatNumber(summary.new_file_count || 0)}`} color="success" variant="outlined" />
            <Chip label={`Deleted files: ${formatNumber(summary.deleted_file_count || 0)}`} color="warning" variant="outlined" />
            {Number(summary.skipped_files || 0) > 0 ? (
              <Chip label={`Unavailable: ${formatNumber(summary.skipped_files)}`} color="warning" variant="outlined" />
            ) : null}
          </Stack>

          <Box sx={{ mb: 1.5 }}>
            <CategoryMetricChips categoryTotals={summary.category_totals} />
          </Box>

          {Number(summary.skipped_files || 0) > 0 ? (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              Unavailable file reasons: {buildReasonLabel(summary.skipped_reason_counts)}
            </Alert>
          ) : null}

          {!hasAnyMetrics ? (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              LOC metrics are unavailable until linked changes have saved diff data for code files.
            </Alert>
          ) : null}

          <Divider sx={{ my: 1.5 }} />

          <Stack spacing={1}>
            {linkedRows.length ? linkedRows.map((row) => (
              <Box
                key={row.change_id || row.dev_ide_change_id || row.title}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 1.25,
                }}
              >
                <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" noWrap>
                      #{row.change_id || row.dev_ide_change_id} {row.title || "Untitled linked change"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.module || "Module unavailable"} - {row.status || "Status unavailable"}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Chip size="small" label={`Added ${getMetricValue(row, "lines_added")}`} color={hasCalculatedMetrics(row) ? "success" : "default"} variant={hasCalculatedMetrics(row) ? "filled" : "outlined"} />
                    <Chip size="small" label={`Removed ${getMetricValue(row, "lines_removed")}`} color={hasCalculatedMetrics(row) ? "warning" : "default"} variant="outlined" />
                    <Chip size="small" label={`Net ${getMetricValue(row, "net_lines")}`} color="primary" variant="outlined" />
                    <Chip size="small" label={`Files ${formatNumber(row.files_changed || 0)}`} variant="outlined" />
                    <Chip size="small" label={`New ${formatNumber(row.change_mode_counts?.new_file || 0)}`} color="success" variant="outlined" />
                    <Chip size="small" label={buildIncompleteLabel(row)} color={hasCalculatedMetrics(row) ? "default" : "warning"} variant="outlined" />
                  </Stack>
                </Stack>
                <Box sx={{ mt: 1 }}>
                  <CategoryMetricChips categoryTotals={row.category_totals} />
                </Box>
                {Number(row.skipped_files || 0) > 0 ? (
                  <Typography variant="caption" color="warning.main" sx={{ display: "block", mt: 0.75 }}>
                    Unavailable reasons: {buildReasonLabel(row.skipped_reason_counts)}
                  </Typography>
                ) : null}
              </Box>
            )) : (
              <Typography variant="body2" color="text.secondary">
                No linked DEV IDE changes are available for LOC metrics.
              </Typography>
            )}
          </Stack>
        </>
      )}
    </Paper>
  );
};

export default FullChangeLocMetricsPanel;
